import { useContext, useEffect, useRef, useState } from "react";
import createStore, { Store } from "./store";
import { Provider } from "react-redux";
import styles from "./index.module.css";
import { ProctoringResultsContext } from "./context";
import ProctoringResultsHeader from "./ProctoringResultsHeader";
import { ProctoringEventsTable } from "./ProctoringEventsTable";
import VideoPlayer from "./VideoPlayer";
import { useGetProctoringEventsQuery, useGetProctoringSessionQuery, useGetVideoUrlsQuery } from "./store/hooks";
import { Checkbox, Link, ListItemText, MenuItem, Skeleton, TextField, Tooltip } from "@mui/material";
import convertEventFormat from "./convertEventFormat";
import { FlagLevel, ProctoringEventInfo, ProctoringEventType } from "./store/slices/proctoringSession";
import i18n from "../../../i18n";
import { FILTER_EVENTS } from "../student-selection/videoplayer/Timestamps/Selector/EventTypeSelector";
import { CoreButton, coreToast, CoreToastContainer } from "@verity/components/core";
import apiClient from "../../../helpers/api-client";
import { openAuthenticatedTab } from "../../../helpers/open-authenticated";
import PopIn from "../../../images/pop-in.svg?react";
import SvgIcon from "@mui/material/SvgIcon";
import { ThemeWrapper } from "@verity/components";

const ALL_FLAG_LEVELS = {
	0: "none",
	1: "minor",
	2: "major",
	3: "severe",
} as const;

export type ProctoringResultsPageProps = {
	courseId: number;
	sessionId: string;
	showFlaggedOnly?: boolean;
	inProgress?: boolean;
	onDataUpdated?: () => void;
};

type UpdateRecordingsRequest = {
	abort: () => void;
	result: Promise<void>;
};

function updateRecordings(proctoringSessionId: string): UpdateRecordingsRequest {
	let aborted = false;
	return {
		abort() {
			aborted = true;
		},
		result: (async () => {
			const { requestId } = await apiClient.proctoringSession.requestVideoConcatenation(proctoringSessionId);
			while (!aborted) {
				await new Promise((resolve) => {
					setTimeout(resolve, 4000);
				});
				const { status } = await apiClient.proctoringSession.getConcatenationRequest(proctoringSessionId, requestId);
				if (status !== "POSTPROCESS_STARTED") {
					if (status === "POSTPROCESS_COMPLETE") {
						return;
					}
					throw new Error(`Postprocess status status was ${status}`);
				}
			}
			throw new Error(`Aborted waiting on concatenation request`);
		})(),
	};
}

function ResultsPageVideo({
	proctoringEventInfo,
	eventsLoading = false,
	currTimestamp = Date.now(),
	refetchEvents,
}: {
	proctoringEventInfo?: ProctoringEventInfo;
	eventsLoading?: boolean;
	currTimestamp?: number;
	refetchEvents: () => Promise<void>;
}) {
	const { proctoringSessionId } = useContext(ProctoringResultsContext);

	const {
		data: webcamVideoUrls,
		isLoading: loadingWebcamVideoUrls,
		refetch: refetchWebcamVideos,
	} = useGetVideoUrlsQuery("webcam");
	const {
		data: screenVideoUrls,
		isLoading: loadingScreenVideoUrls,
		refetch: refetchScreenVideos,
	} = useGetVideoUrlsQuery("screencapture");

	const updateRecordingsRequestRef = useRef<UpdateRecordingsRequest | undefined>();
	useEffect(() => () => updateRecordingsRequestRef.current?.abort(), []);

	if (eventsLoading || loadingWebcamVideoUrls || loadingScreenVideoUrls) {
		return <Skeleton variant="rectangular" height="100%" sx={{ flex: 1 }} />;
	}

	const proctoringOptions = proctoringEventInfo?.proctoringOptions;
	const audioOnly =
		proctoringOptions &&
		proctoringOptions.videoRecordingMode === "off" &&
		proctoringOptions.audioRecordingMode !== "off";

	return (
		<div className={styles.videoPanel}>
			<Tooltip title={i18n.t("result.components.resultsPage.indexTSX.updateVideoTooltip")}>
				<Link
					component="button"
					className={styles.updateVideoButton}
					onClick={async () => {
						if (proctoringSessionId) {
							updateRecordingsRequestRef.current?.abort();
							updateRecordingsRequestRef.current = updateRecordings(proctoringSessionId);
							await updateRecordingsRequestRef.current.result;
							await Promise.all([refetchEvents(), refetchWebcamVideos().unwrap(), refetchScreenVideos().unwrap()]);
						}
					}}
				>
					{i18n.t("result.components.resultsPage.indexTSX.updateVideo")}
				</Link>
			</Tooltip>
			<VideoPlayer
				sessionId={proctoringSessionId}
				webcamVidSources={webcamVideoUrls}
				screenVidSources={screenVideoUrls}
				events={proctoringEventInfo?.events.map(convertEventFormat)}
				sessionEvents={proctoringEventInfo?.sessionEvents.map(convertEventFormat)}
				duration={
					proctoringEventInfo
						? (proctoringEventInfo?.endProctoringTime ?? currTimestamp) - (proctoringEventInfo.startProctoringTime ?? 0)
						: 0
				}
				timeOffset={
					proctoringEventInfo
						? Math.max(0, (proctoringEventInfo.startQuizTime ?? 0) - (proctoringEventInfo.startProctoringTime ?? 0))
						: 0
				}
				startDate={new Date(proctoringEventInfo?.startProctoringTime ?? 0)}
				audioOnly={audioOnly}
				hideCameraVideo={audioOnly && proctoringOptions && proctoringOptions.recordScreen}
			/>
		</div>
	);
}

function EventFiltersRow({
	eventTypes,
	setEventTypes,
	flagLevels,
	setFlagLevels,
}: {
	eventTypes: ProctoringEventType[];
	setEventTypes: (types: ProctoringEventType[]) => void;
	flagLevels: FlagLevel[];
	setFlagLevels: (levels: FlagLevel[]) => void;
}) {
	const { config } = useContext(ProctoringResultsContext);
	const { showFlaggedOnly } = config;

	const enabledFlagLevels = (
		showFlaggedOnly
			? Object.entries(ALL_FLAG_LEVELS).filter(([, severity]) => severity !== "none")
			: Object.entries(ALL_FLAG_LEVELS)
	).map(([flagLevel, severity]) => [Number(flagLevel), severity] as [FlagLevel, (typeof ALL_FLAG_LEVELS)[FlagLevel]]);
	const enabledEventTypes = showFlaggedOnly ? FILTER_EVENTS.filter(({ type }) => type === "flagEvent") : FILTER_EVENTS;

	const eventTypeLabel = showFlaggedOnly
		? i18n.t("result.components.resultsPage.indexTSX.flagType")
		: i18n.t("result.components.resultsPage.indexTSX.eventType");

	return (
		<div className={styles.eventFilterRow}>
			<TextField
				select
				value={eventTypes}
				aria-label={eventTypeLabel}
				SelectProps={{
					multiple: true,

					displayEmpty: true,
					renderValue: () => eventTypeLabel,
					MenuProps: {
						slotProps: { paper: { sx: { maxHeight: "33%" } } },
						transformOrigin: { vertical: "top", horizontal: "left" },
						anchorOrigin: { vertical: "bottom", horizontal: "left" },
					},
				}}
				onChange={(event) => {
					const { value } = event.target;
					setEventTypes((value === "string" ? value.split(",") : value) as unknown as ProctoringEventType[]);
				}}
			>
				{enabledEventTypes.map(({ name }) => (
					<MenuItem key={name} value={name}>
						<Checkbox checked={eventTypes.includes(name)} tabIndex={-1} disableRipple edge="start" />
						<ListItemText primary={name} />
					</MenuItem>
				))}
			</TextField>
			<TextField
				select
				value={flagLevels}
				aria-label={i18n.t("result.components.resultsPage.indexTSX.flagLevel")}
				SelectProps={{
					multiple: true,
					displayEmpty: true,
					renderValue: () => i18n.t("result.components.resultsPage.indexTSX.flagLevel"),
				}}
				onChange={(event) => {
					const { value } = event.target;
					setFlagLevels((value === "string" ? value.split(",") : value) as unknown as FlagLevel[]);
				}}
			>
				{enabledFlagLevels.map(([flagLevel, severityName]) => (
					<MenuItem key={flagLevel} value={flagLevel}>
						<Checkbox checked={flagLevels.includes(flagLevel)} tabIndex={-1} disableRipple edge="start" />
						<ListItemText primary={i18n.t(`result.components.resultsPage.indexTSX.${severityName}`)} />
					</MenuItem>
				))}
			</TextField>
		</div>
	);
}

function ResultsPageContent() {
	const [eventTypes, setEventTypes] = useState<ProctoringEventType[]>([]);
	const [flagLevels, setFlagLevels] = useState<FlagLevel[]>([]);
	const {
		data: proctoringEventInfo,
		isLoading,
		isError,
		fulfilledTimeStamp,
		refetch: refetchEvents,
	} = useGetProctoringEventsQuery(
		{
			flagLevels: flagLevels,
			eventTypes,
		},
		{ pollingInterval: 10000 },
	);

	const { proctoringSessionId } = useContext(ProctoringResultsContext);
	const { data: proctoringSession } = useGetProctoringSessionQuery();
	const liveSessionId = proctoringSession?.liveSessionId;

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.resultsPage }}>
			<CoreToastContainer />
			<ProctoringResultsHeader />
			<div className={styles.content}>
				<ResultsPageVideo
					proctoringEventInfo={proctoringEventInfo}
					eventsLoading={isLoading}
					currTimestamp={fulfilledTimeStamp}
					refetchEvents={async () => {
						await refetchEvents().unwrap();
					}}
				/>
				<div className={styles.eventsPanel}>
					<div className={styles.eventTopRow}>
						<EventFiltersRow
							eventTypes={eventTypes}
							setEventTypes={setEventTypes}
							flagLevels={flagLevels}
							setFlagLevels={setFlagLevels}
						/>
						{proctoringEventInfo?.proctoringOptions?.isLivePopIn && liveSessionId && (
							<CoreButton
								onClick={async () => {
									try {
										await apiClient.liveClient.registerSelfAsProctor(liveSessionId);
										openAuthenticatedTab(
											`/live/pop-in.html?id=${liveSessionId}&proctoringSessionId=${proctoringSessionId}`,
										)?.focus();
									} catch (e) {
										console.error(e);
										coreToast.error(i18n.t("result.components.resultsPage.indexTSX.joinPopInFailed"));
									}
								}}
								startIcon={
									<SvgIcon>
										<PopIn />
									</SvgIcon>
								}
								sx={{ flexShrink: 0 }}
							>
								{i18n.t("result.components.resultsPage.indexTSX.livePopIn")}
							</CoreButton>
						)}
					</div>
					<ProctoringEventsTable
						className={`${styles.eventsTable} ${isLoading || isError || !proctoringEventInfo || proctoringEventInfo.events.length === 0 ? styles.noResults : ""}`}
						proctoringEventInfo={proctoringEventInfo}
						isLoading={isLoading}
						isError={isError}
					/>
				</div>
			</div>
		</ThemeWrapper>
	);
}

export default function ProctoringResultsPage({
	courseId,
	sessionId,
	showFlaggedOnly,
	inProgress,
	onDataUpdated,
}: ProctoringResultsPageProps) {
	const store = useRef<Store>(createStore({ onDataUpdated }));

	return (
		<Provider store={store.current}>
			<ProctoringResultsContext.Provider
				value={{
					courseId,
					proctoringSessionId: sessionId,
					config: { showFlaggedOnly: showFlaggedOnly ?? false, sessionInProgress: inProgress ?? false },
				}}
			>
				<ResultsPageContent />
			</ProctoringResultsContext.Provider>
		</Provider>
	);
}
